import React, { useState, useEffect } from "react";
import PageLayout from "components/layout/PageLayout";
import { useTranslation } from "react-i18next";
import { useGetMainSettingsQuery, useSetMainSettingsMutation } from "./api/mainSettingsApiSlice";
import SettingsCard from "components/commons/SettingsCard";
import SuccessMessage from "components/commons/SuccessMessage";

export default function MainSettings() {
    const [t] = useTranslation("global");

    const [settings, setSettings] = useState();
    const [statusMessage, setStatusMessage] = useState("");

    const {
        data,
        isFetching,
        isLoading,
        isSuccess,
        isError,
        error,
        refetch
    } = useGetMainSettingsQuery();

    const [saveSettings, {
        isLoading: saveIsLoading,
        isSuccess: saveIsSuccess,
        isError: saveIsError,
        error: saveError,
        reset: resetSaveStatus
    }] = useSetMainSettingsMutation();

    useEffect(() => {
        if (data && isSuccess) {
            setSettings(data);
        }
    }, [data, isSuccess]);

    const handleChange = (key, value) => {
        setSettings((prevSettings) => ({
            ...prevSettings,
            [key]: value,
        }));
        resetSaveStatus();
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        saveSettings(settings);
    };

    const content = (
        <div>
            {isError ? (
                <p>{t("Failed to load settings.")} {error?.data?.message || error?.status}</p>
            ) : isSuccess && settings ? (
                <form onSubmit={handleSubmit}>
                    {saveIsSuccess && <SuccessMessage />}
                    <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3 mb-4">
                        <div className="col">
                            <SettingsCard
                                label={t("setting.color.title")}
                                description={t("setting.color.desc")}
                                logo={<i className="fa-solid fa-palette"></i>}
                                value={settings.color}
                                onChange={(e) => handleChange("color", e.target.value)}
                            />
                        </div>
                        <div className="col">
                            <SettingsCard
                                label={t("setting.session_duration.title")}
                                description={t("setting.session_duration.desc")}
                                logo={<i className="fa-solid fa-user-clock"></i>}
                                value={settings.session_duration_hours}
                                measureUnit={t("unit.hours")}
                                onChange={(e) => handleChange("session_duration_hours", parseInt(e.target.value, 10))}
                                inputType="number"
                            />
                        </div>
                        <div className="col">
                            <SettingsCard
                                label={t("setting.log_session_event.title")}
                                description={t("setting.log_session_event.desc")}
                                logo={<i className="fa-solid fa-door-open"></i>}
                                value={settings.session_events_log}
                                onChange={(e) => handleChange("session_events_log", e.target.checked)}
                                inputType="checkbox"
                            />
                        </div>
                        <div className="col">
                            <SettingsCard
                                label={t("setting.notifications_retention.title")}
                                description={t("setting.notifications_retention.desc")}
                                logo={<i className="fa-solid fa-bell"></i>}
                                value={settings.notification_retention_days}
                                measureUnit={t("unit.days_other")}
                                onChange={(e) => handleChange("notification_retention_days", parseInt(e.target.value, 10))}
                                inputType="number"
                            />
                        </div>
                        <div className="col">
                            <SettingsCard
                                label={t("setting.send_notification_emails.title")}
                                description={t("setting.send_notification_emails.desc")}
                                logo={<i class="fa-solid fa-envelope-circle-check"></i>}
                                value={settings.send_notification_email}
                                onChange={(e) => handleChange("send_notification_email", e.target.checked)}
                                inputType="checkbox"
                            />
                        </div>
                    </div>
                    <button type="submit" className="btn btn-primary"><i className="fa-solid fa-floppy-disk"></i> {t("action.save")}</button>


                </form>
            ) : <></>}
        </div>
    );

    return (
        <PageLayout
            pageTitle={t("Main Settings")}
            bodyContent={content}
            onPageRefresh={refetch}
            isLoadingContent={saveIsLoading || isFetching}
            isLoadingPage={isLoading}
            isError={saveIsError || isError}
            errorMsg={saveError || error}
        />
    );
}
